/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { CalendarDate } from '@internationalized/date';

import { localToday } from 'sarsaparilla';

import amexSrc from '../../img/credit-cards/amex.svg';
import discoverSrc from '../../img/credit-cards/discover.svg';
import mastercardSrc from '../../img/credit-cards/mastercard.svg';
import visaSrc from '../../img/credit-cards/visa.svg';
import jcbSrc from '../../img/credit-cards/jcb.svg';
import unionPaySrc from '../../img/credit-cards/unionpay.svg';
import type { SavedCard, CardBrand } from '../../api/digital-wallet-types';

export const imageSrcMap: Record<CardBrand, string> = {
    VISA: visaSrc,
    DISCOVER: discoverSrc,
    AMEX: amexSrc,
    MASTERCARD: mastercardSrc,
    JCB: jcbSrc,
    UNIONPAY: unionPaySrc,
} as const;

const cardIssuerBrandMap: Record<string, CardBrand> = {
    'american-express': 'AMEX',
    mastercard: 'MASTERCARD',
    discover: 'DISCOVER',
    visa: 'VISA',
    jcb: 'JCB',
    unionpay: 'UNIONPAY',
} as const;

const cardBrandDisplayNameMap: Record<CardBrand, string> = {
    VISA: 'Visa',
    DISCOVER: 'Discover',
    AMEX: 'American Express',
    MASTERCARD: 'Mastercard',
    JCB: 'JCB',
    UNIONPAY: 'UnionPay',
} as const;

/**
 * Takes the month and year as two digit strings and returns a boolean indicating
 * if the card is expired (true) or not (false)
 */
export function getIsCardExpired(combinedExpDate: string) {
    const expMonth = combinedExpDate.substring(0, 2);
    const expYear = combinedExpDate.substring(2, 4);
    const month = Number(expMonth);
    const year = Number(expYear) + 2000;

    // Assume the expire date is midnight on the last of the month,
    // so we use the first of the next month for the comparison
    const expireCalendarDate = new CalendarDate(year, month, 1).add({ months: 1 });

    const difference = expireCalendarDate.compare(localToday);
    return difference <= 0;
}

/**
 * Converts a credit card brand or issuer identifier to its formatted display name.
 * If the identifier is not recognized, returns 'Unknown Card'.
 */
export function getCreditCardDisplayName(identifier: string) {
    // Try direct CardBrand match
    if (identifier in cardBrandDisplayNameMap) {
        return cardBrandDisplayNameMap[identifier as CardBrand];
    }
    // Try ALLOWED_WALLET_CARD_ISSUERS mapping
    if (identifier in cardIssuerBrandMap) {
        const brand = cardIssuerBrandMap[identifier];
        return cardBrandDisplayNameMap[brand] || 'Unknown Card';
    }
    return 'Unknown Card';
}

/**
 * Takes an array of credit card identifiers and returns a formatted string
 * of display names.
 */
export function formatCreditCardList(identifiers?: string[]) {
    if (!identifiers || identifiers.length === 0) return '';

    // Remove duplicates
    const uniqueIdentifiers = Array.from(new Set(identifiers));
    // Format names
    const names = uniqueIdentifiers.map(getCreditCardDisplayName);

    if (names.length === 1) return `(${names[0]})`;
    if (names.length === 2) return `(${names[0]} and ${names[1]})`;
    return `(${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]})`;
}

export function useSavedCreditCardSorter(data?: SavedCard[]) {
    return React.useMemo(() => {
        if (!data || !data.length) return [];

        const newData: Array<SavedCard> = [...data];

        // Sort so expired cards go to the bottom; default card goes to the top
        return newData
            .sort((a, b) => {
                const isExpiredA = getIsCardExpired(a.card_expiration_date);
                const isExpiredB = getIsCardExpired(b.card_expiration_date);

                if (isExpiredA > isExpiredB) return 1;
                if (isExpiredA < isExpiredB) return -1;

                return 0;
            })
            .sort((a, b) => {
                const isDefaultA = Boolean(a.is_default);
                const isDefaultB = Boolean(b.is_default);

                if (isDefaultA > isDefaultB) return -1;
                if (isDefaultA < isDefaultB) return 1;

                return 0;
            });
    }, [data]);
}

export function makeSuggestedSavedCardNickname({
    issuer,
    creditCardNumber,
    isValid,
}: {
    issuer?: string;
    creditCardNumber?: string;
    isValid?: boolean;
}) {
    const issueNameMap: Record<string, string> = {
        'american-express': 'Amex',
        mastercard: 'Mastercard',
        discover: 'Discover',
        visa: 'Visa',
        jcb: 'JCB',
        unionpay: 'UnionPay',
    };

    let suggestedName =
        issuer && issueNameMap[issuer] ? `My ${issueNameMap[issuer]}` : 'My Credit Card';

    if (creditCardNumber && isValid) {
        const last4 = creditCardNumber.slice(-4);
        suggestedName += ` ending in ${last4}`;
    }

    return suggestedName;
}

// cSpell:ignore UNIONPAY
